import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "@/context/AppProviders";
import { MaskedLine, FadeIn, Overline } from "@/components/Kinetic";
import { Marquee } from "@/components/Marquee";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const PORTRAIT_IMG = "/images/msah-portrait.webp";
const STAGE_IMG = "https://images.unsplash.com/photo-1475721027785-f74eccf877e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const TIKTOK_URL = "https://www.tiktok.com/@m.sah23";
const FACEBOOK_URL = "https://www.facebook.com/share/1CvqGTWzpz/";

export default function Home() {
  const { t } = useLanguage();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden" data-testid="hero-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 w-full grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative z-10">
            <FadeIn y={12}><Overline>{t.home.overline}</Overline></FadeIn>
            <h1 className="mt-8 font-display font-semibold tracking-tighter leading-[0.9] text-[clamp(3.2rem,9vw,8.5rem)] text-ink">
              <MaskedLine delay={0.15}>{t.home.line1}</MaskedLine>
              <MaskedLine delay={0.3}>{t.home.line2}</MaskedLine>
              <MaskedLine delay={0.45} innerClassName="font-serif italic font-light text-ember tracking-tight">{t.home.line3}</MaskedLine>
            </h1>
            <FadeIn delay={0.7} className="mt-10 max-w-xl">
              <p className="text-base md:text-lg text-ink/60 leading-relaxed">{t.home.sub}</p>
            </FadeIn>
            <FadeIn delay={0.85} className="mt-12 flex flex-wrap items-center gap-5">
              <Link to="/formation" data-testid="hero-cta-formation" className="btn-glow bg-ember hover:bg-emberlight text-white px-8 py-4 text-sm tracking-wide transition-colors duration-300 flex items-center gap-2">
                {t.home.cta} <ArrowUpRight size={16} />
              </Link>
              <a href="#qui-suis-je" data-testid="hero-cta-about" className="link-line text-sm text-ink/60 hover:text-ink transition-colors py-4">
                {t.home.cta2}
              </a>
            </FadeIn>

            <FadeIn delay={1} className="mt-16 flex gap-12">
              <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" data-testid="stat-tiktok" className="group">
                <p className="font-display font-semibold text-4xl md:text-5xl text-ink tracking-tight group-hover:text-ember transition-colors">+100K</p>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-ink/50">{t.home.stat1} <ArrowUpRight size={11} className="inline" /></p>
              </a>
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="stat-facebook" className="group">
                <p className="font-display font-semibold text-4xl md:text-5xl text-ink tracking-tight group-hover:text-ember transition-colors">+100K</p>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-ink/50">{t.home.stat2} <ArrowUpRight size={11} className="inline" /></p>
              </a>
            </FadeIn>
          </div>

          <div className="lg:col-span-5 relative hidden lg:block">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.33, 1, 0.68, 1] }}
              className="hero-clip relative aspect-[3/4] overflow-hidden"
            >
              <motion.img src={STAGE_IMG} alt="M.SAH — prise de parole" style={{ y: imgY }} className="w-full h-[120%] object-cover scale-110" data-testid="hero-image" />
              <div className="absolute inset-0 ring-1 ring-inset ring-ink/10" />
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
              className="absolute -bottom-6 -left-10 font-serif italic text-xl text-ink/50"
            >
              — M.SAH
            </motion.p>
          </div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="absolute bottom-8 left-6 md:left-10 flex items-center gap-3 text-ink/40 text-xs tracking-[0.25em] uppercase">
          <ArrowDown size={14} className="animate-bounce" /> {t.home.scroll}
        </motion.div>
      </section>

      <Marquee items={t.marquee} />

      {/* ABOUT */}
      <section id="qui-suis-je" className="py-28 md:py-40" data-testid="about-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <FadeIn className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden hero-clip">
              <img src={PORTRAIT_IMG} alt="M.SAH portrait" className="w-full h-full object-cover object-[50%_15%]" data-testid="about-portrait" />
              <div className="absolute inset-0 ring-1 ring-inset ring-ink/10" />
            </div>
          </FadeIn>
          <div className="lg:col-span-7">
            <FadeIn><Overline>{t.home.about.overline}</Overline></FadeIn>
            <FadeIn delay={0.1}>
              <h2 className="mt-6 font-display font-semibold tracking-tighter text-4xl md:text-6xl text-ink leading-[0.95]">
                {t.home.about.title} <span className="font-serif italic font-light text-ember">{t.home.about.titleSerif}</span>
              </h2>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="mt-8 text-ink/60 leading-relaxed text-base md:text-lg max-w-2xl">{t.home.about.p1}</p>
              <p className="mt-5 text-ink/60 leading-relaxed text-base md:text-lg max-w-2xl">{t.home.about.p2}</p>
            </FadeIn>
            <FadeIn delay={0.3} className="mt-10 flex flex-wrap items-center gap-4">
              <span className="text-xs uppercase tracking-[0.25em] text-ink/40">{t.home.about.social}</span>
              <a href={TIKTOK_URL} target="_blank" rel="noopener noreferrer" data-testid="about-tiktok-link" className="border border-ink/20 hover:border-ember hover:text-ember text-ink/70 px-5 py-2.5 text-sm transition-colors duration-300">TikTok</a>
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="about-facebook-link" className="border border-ink/20 hover:border-ember hover:text-ember text-ink/70 px-5 py-2.5 text-sm transition-colors duration-300">Facebook</a>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-28 md:py-40 border-t border-ink/10" data-testid="mission-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <FadeIn><Overline>{t.home.mission.overline}</Overline></FadeIn>
          <FadeIn delay={0.1}>
            <h2 className="mt-6 font-display font-semibold tracking-tighter text-4xl md:text-6xl text-ink">{t.home.mission.title}</h2>
          </FadeIn>
          <div className="mt-20 md:mt-28 space-y-24 md:space-y-32">
            {t.home.mission.chapters.map((ch, i) => (
              <FadeIn key={ch.num} className="grid md:grid-cols-12 gap-8 items-start">
                <div className={`md:col-span-4 ${i % 2 === 1 ? "md:order-2 md:col-start-9" : ""}`}>
                  <span className="font-serif italic text-7xl md:text-8xl text-ember/80 leading-none">{ch.num}</span>
                </div>
                <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-1 md:col-start-1" : "md:col-start-6"}`}>
                  <h3 className="font-display font-medium text-2xl md:text-4xl text-ink tracking-tight">{ch.title}</h3>
                  <p className="mt-5 text-ink/60 leading-relaxed max-w-xl text-base md:text-lg">{ch.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* FORMATION TEASER */}
      <section className="py-28 md:py-36 border-t border-ink/10 bg-ink" data-testid="formation-teaser">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10">
          <FadeIn><Overline>{t.home.formationTeaser.overline}</Overline></FadeIn>
          <div className="mt-8 grid lg:grid-cols-12 gap-12 items-end">
            <FadeIn delay={0.1} className="lg:col-span-8">
              <h2 className="font-display font-semibold tracking-tighter text-4xl md:text-6xl text-white leading-[0.95]">
                {t.home.formationTeaser.title}
              </h2>
              <p className="mt-6 text-white/50 text-sm uppercase tracking-[0.25em]">{t.home.formationTeaser.sub}</p>
            </FadeIn>
            <FadeIn delay={0.2} className="lg:col-span-4 flex lg:justify-end">
              <Link to="/formation" data-testid="teaser-cta" className="btn-glow bg-ember hover:bg-emberlight text-white px-8 py-4 text-sm tracking-wide transition-colors duration-300 flex items-center gap-2">
                {t.home.formationTeaser.cta} <ArrowUpRight size={16} />
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
